import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import functions from '../../../pages/index'
import { PermissionPolicyOption } from '../../../../domain/value-object/PermissionPolicy'
import { RoleRow } from '../../../pages/Roles/model/RoleRow'
import {
  Operation,
  OperationI18nLabelKey,
  scopeVoService,
  ScopeVO,
} from '../../../../domain/value-object/ScopeVO'
import { DEPRECATED_FUNCTION_DISPLAY_ORDER } from '../../../../infrastructure/api/application-functions'

export const useAvailableFunctionsTooltipGetter = () => {
  const intl = useIntl()
  const noneLabel = useMemo(() => intl.formatMessage({ id: 'none' }), [intl])
  const allScreensLabel = useMemo(
    () => intl.formatMessage({ id: 'permissionPolicies.allScreens' }),
    [intl]
  )

  const getOperationStrength = useCallback((operation: Operation): number => {
    const strengthOrder = [
      Operation.LIST,
      Operation.GET,
      Operation.UPDATE,
      Operation.CREATE,
      Operation.DELETE,
      Operation.ALL,
    ]
    return strengthOrder.indexOf(operation)
  }, [])

  const getDirectoryDisplayName = useCallback(
    (directory: { name: string; externalId: string }) => {
      if (directory.externalId === 'tenant') {
        return intl.formatMessage({ id: 'permissionPolicies.personalMenu' })
      }
      return directory.name
    },
    [intl]
  )

  const getDirectoryPriority = useCallback((externalId: string): number => {
    if (externalId === 'tenant') return 0
    if (externalId === 'project') return 1
    return 2
  }, [])

  const sortDirectories = useCallback(
    <T extends { externalId: string; displayOrder: number }>(
      a: T,
      b: T
    ): number => {
      const priorityA = getDirectoryPriority(a.externalId)
      const priorityB = getDirectoryPriority(b.externalId)
      if (priorityA !== priorityB) {
        return priorityA - priorityB
      }
      return a.displayOrder - b.displayOrder
    },
    [getDirectoryPriority]
  )
  const getAvailableFunctionInfo = useCallback(
    (
      s: ScopeVO
    ):
      | { functionName: string; operation: Operation; text: string }
      | undefined => {
      if (!s.applicationFunction) {
        return undefined
      }
      if (!functions[s.applicationFunction.externalId]) {
        // feature-flagで非表示にされる機能を除くための条件
        return undefined
      }
      // TODO 判定条件見直す
      if (
        s.applicationFunction.displayOrder === DEPRECATED_FUNCTION_DISPLAY_ORDER
      ) {
        return undefined
      }

      const functionName = s.applicationFunction.name
      const operationLabel = intl.formatMessage({
        id: OperationI18nLabelKey[s.operation],
      })
      return {
        functionName,
        operation: s.operation,
        text: `${operationLabel} ${functionName}`,
      }
    },
    [intl]
  )

  const getAvailableFunctionsTooltipOfPermissionPolicy = useCallback(
    (permissionPolicyOption: PermissionPolicyOption) => {
      const tooltipLines: string[] = []
      if (permissionPolicyOption.description) {
        tooltipLines.push(permissionPolicyOption.description)
        tooltipLines.push('')
      }

      // 全画面が利用可能かチェック
      const hasAllScreens = permissionPolicyOption.scopes.some(s =>
        scopeVoService.areAvailableAllFunctions(s)
      )

      if (hasAllScreens) {
        tooltipLines.push(allScreensLabel)
        return tooltipLines.join('\n')
      }

      // ディレクトリごとにグループ化し、機能名ごとに最も強いoperationを保持
      const directoryMap = new Map<
        string,
        Map<
          string,
          { displayOrder: number; operation: Operation; text: string }
        >
      >()

      permissionPolicyOption.scopes.forEach(s => {
        if (
          s.applicationFunction &&
          functions[s.applicationFunction.externalId]
        ) {
          const functionInfo = getAvailableFunctionInfo(s)
          if (functionInfo) {
            const dirKey = s.applicationFunction.directory.uuid
            const funcName = functionInfo.functionName

            if (!directoryMap.has(dirKey)) {
              directoryMap.set(dirKey, new Map())
            }

            const funcMap = directoryMap.get(dirKey)!
            const existing = funcMap.get(funcName)

            // 既存のエントリがない、または現在のoperationの方が強い場合のみ更新
            if (
              !existing ||
              getOperationStrength(functionInfo.operation) >
                getOperationStrength(existing.operation)
            ) {
              funcMap.set(funcName, {
                displayOrder: s.applicationFunction.displayOrder,
                operation: functionInfo.operation,
                text: functionInfo.text,
              })
            }
          }
        }
      })

      // ディレクトリをdisplayOrderでソート
      const sortedDirectories = Array.from(directoryMap.entries())
        .map(([dirUuid, funcMap]) => {
          const directory = permissionPolicyOption.scopes.find(
            s =>
              s.applicationFunction &&
              s.applicationFunction.directory.uuid === dirUuid
          )?.applicationFunction?.directory
          // 機能をdisplayOrderでソート
          const items = Array.from(funcMap.values()).sort(
            (a, b) => a.displayOrder - b.displayOrder
          )
          return {
            directory,
            items,
          }
        })
        .filter(
          (
            entry
          ): entry is {
            directory: NonNullable<typeof entry.directory>
            items: typeof entry.items
          } => entry.directory !== undefined
        )
        .sort((a, b) => sortDirectories(a.directory, b.directory))

      // ツールチップに追加
      sortedDirectories.forEach(({ directory, items }) => {
        const displayName = getDirectoryDisplayName(directory)
        tooltipLines.push(`[${displayName}]`)
        items.forEach(item => {
          tooltipLines.push(`  ${item.text}`)
        })
      })

      return tooltipLines.join('\n')
    },
    [
      allScreensLabel,
      getAvailableFunctionInfo,
      getDirectoryDisplayName,
      getOperationStrength,
      sortDirectories,
    ]
  )
  const getAvailableFunctionsTooltipOfRole = useCallback(
    (role: RoleRow, permissionPolicyOptions: PermissionPolicyOption[]) => {
      const selectedPolicies = permissionPolicyOptions.filter(p =>
        role.permissionPolicyUuids.includes(p.uuid)
      )

      // 全画面が利用可能かチェック
      const hasAllScreens = selectedPolicies.some(policy =>
        policy.scopes.some(s => scopeVoService.areAvailableAllFunctions(s))
      )

      if (hasAllScreens) {
        return allScreensLabel
      }

      // ディレクトリごとにグループ化し、機能名ごとに最も強いoperationを保持
      const directoryMap = new Map<
        string,
        Map<
          string,
          { displayOrder: number; operation: Operation; text: string }
        >
      >()
      const directoryInfo = new Map<
        string,
        { name: string; externalId: string; displayOrder: number }
      >()

      selectedPolicies.forEach(policy => {
        policy.scopes.forEach(scope => {
          if (
            scope.applicationFunction &&
            functions[scope.applicationFunction.externalId]
          ) {
            const functionInfo = getAvailableFunctionInfo(scope)
            if (functionInfo) {
              const dirUuid = scope.applicationFunction.directory.uuid
              const funcName = functionInfo.functionName

              if (!directoryMap.has(dirUuid)) {
                directoryMap.set(dirUuid, new Map())
                directoryInfo.set(dirUuid, {
                  name: scope.applicationFunction.directory.name,
                  externalId: scope.applicationFunction.directory.externalId,
                  displayOrder:
                    scope.applicationFunction.directory.displayOrder,
                })
              }

              const funcMap = directoryMap.get(dirUuid)!
              const existing = funcMap.get(funcName)

              // 既存のエントリがない、または現在のoperationの方が強い場合のみ更新
              if (
                !existing ||
                getOperationStrength(functionInfo.operation) >
                  getOperationStrength(existing.operation)
              ) {
                funcMap.set(funcName, {
                  displayOrder: scope.applicationFunction.displayOrder,
                  operation: functionInfo.operation,
                  text: functionInfo.text,
                })
              }
            }
          }
        })
      })

      const tooltip: string[] = []

      if (directoryMap.size === 0) {
        tooltip.push(noneLabel)
      } else {
        // ディレクトリをdisplayOrderでソート
        const sortedDirectories = Array.from(directoryMap.entries())
          .map(([dirUuid, funcMap]) => ({
            uuid: dirUuid,
            info: directoryInfo.get(dirUuid)!,
            funcMap,
          }))
          .sort((a, b) => sortDirectories(a.info, b.info))

        sortedDirectories.forEach(({ info, funcMap }) => {
          const displayName = getDirectoryDisplayName(info)
          tooltip.push(`[${displayName}]`)

          // 機能をdisplayOrderでソート
          const sortedFunctions = Array.from(funcMap.values()).sort(
            (a, b) => a.displayOrder - b.displayOrder
          )

          sortedFunctions.forEach(func => {
            tooltip.push(`  ${func.text}`)
          })
        })
      }

      return tooltip.join('\n')
    },
    [
      allScreensLabel,
      getAvailableFunctionInfo,
      getDirectoryDisplayName,
      getOperationStrength,
      noneLabel,
      sortDirectories,
    ]
  )

  return {
    getAvailableFunctionsTooltipOfPermissionPolicy,
    getAvailableFunctionsTooltipOfRole,
  }
}
